<template>
  <div
    class="navigation-arrow"
    :class="{
      'navigation-arrow--hidden': !visible,
    }"
  >
    <NebulaButton
      v-if="showButton"
      :class="{
        [`navigation-arrow__button navigation-arrow__button--${action}`]: true,
        'navigation-arrow__mystery-theme-button': type === 'mystery',
        [`navigation-arrow__mystery-theme-button--${action}`]: type === 'mystery',
      }"
      :aria-label="ariaLabel"
      :icon-left="action === 'prev' ? 'caret-left' : 'caret-right'"
      :is-disabled="isDisabled"
      :disabled="isDisabled"
      @click="action === 'prev' ? goToPrevPage() : goToNextPage()"
    />
  </div>
</template>

<script>
import { throttle } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import { NebulaButton } from '@discoveryedu/nebula-components';
import { isMobileOrTablet } from '@/lib/utils';
import {
  useEditorStore,
  useTrackingStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  name: 'NavigationArrows',
  components: {
    NebulaButton,
  },
  props: {
    container: {
      type: HTMLElement,
      default: null,
    },
    mode: {
      type: String,
      required: true,
    },
    type: {
      type: String,
      default: 'default',
    },
    action: {
      type: String,
      default: 'prev', // prev, next
    },
  },
  data() {
    return {
      visible: true,
      noActivityTimeout: null,
      isMobileOrTablet,
    };
  },
  created() {
    this.onMouseMove = throttle((event) => {
      if (!this.container) return;
      // grab the bounding rect for the navigation container
      const bounds = this.container.getBoundingClientRect();

      // calculate 20% or 50px, whichever is bigger
      const space = Math.max(bounds.width * 0.2, 50);

      // If we mouse over the left or right 20% of the container bounds, show the navigation overlay
      const inVerticalBounds = event.clientY >= bounds.top && event.clientY <= bounds.bottom;
      const inHorizontalBounds = (event.clientX <= bounds.left + space && event.clientX >= bounds.left)
        || (event.clientX >= bounds.right - space && event.clientX <= bounds.right);
      if (inVerticalBounds && inHorizontalBounds) {
        this.clearNoActivityTimeout();
        this.visible = true;
      } else if (!this.noActivityTimeout) {
        this.startNoActivityTimeout();
      }
    }, 100);
  },
  mounted() {
    if (this.activityTimeoutEnabled) {
      this.onActivityTimeoutEnabled();
    }
  },
  beforeUnmount() {
    this.removeDocumentHandlers();
    this.onActivityTimeoutDisabled();
  },
  watch: {
    activityTimeoutEnabled() {
      if (this.activityTimeoutEnabled) {
        this.onActivityTimeoutEnabled();
      } else {
        this.onActivityTimeoutDisabled();
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'draft',
      'currentPageIndex',
      'currentModalCanvasId',
      'draftType',
      'pagesSorted',
    ]),
    ariaLabel() {
      if (this.action === 'prev') {
        return this.draftType === 'board'
          ? this.$t('Previous Page')
          : this.$t('Previous Slide');
      }
      return this.draftType === 'board'
        ? this.$t('Next Page')
        : this.$t('Next Slide');
    },
    isDisabled() {
      return !!this.currentModalCanvasId;
    },
    showButton() {
      return (!this.nextPageDisabled && this.action === 'next')
        || (!this.prevPageDisabled && this.action === 'prev');
    },
    activityTimeoutEnabled() {
      return !isMobileOrTablet() && this.type !== 'mystery';
    },
    prevPageDisabled() {
      return this.currentPageIndex === 0;
    },
    nextPageDisabled() {
      return this.currentPageIndex === this.pagesSorted.length - 1;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.VIEW_PREV_PAGE,
      types.VIEW_NEXT_PAGE,
    ]),
    ...mapActions(useTrackingStore, [
      types.SEND_EVENT,
    ]),
    addDocumentHandlers() {
      document.addEventListener('mousemove', this.onMouseMove);
      document.addEventListener('keyup', this.onKeyup);
    },
    removeDocumentHandlers() {
      document.removeEventListener('mousemove', this.onMouseMove);
      document.removeEventListener('keyup', this.onKeyup);
    },
    async goToPrevPage() {
      // Navigate
      await this[types.VIEW_PREV_PAGE]();

      // Send pageview event
      if (this.mode === 'VIEW') {
        this[types.SEND_EVENT]({
          referenceId: this.draft.id,
          eventType: 'nav.prev',
          referenceType: 'creation',
        });
      }
    },
    async goToNextPage() {
      // Navigate
      await this[types.VIEW_NEXT_PAGE]();

      // Send pageview event
      if (this.mode === 'VIEW') {
        this[types.SEND_EVENT]({
          referenceId: this.draft.id,
          eventType: 'nav.next',
          referenceType: 'creation',
        });
      }
    },
    onKeyup(event) {
      if ((event.key === 'Tab' || event.keyCode === 9)) {
        this.clearNoActivityTimeout();
        this.visible = true;
      }
    },
    onActivityTimeoutEnabled() {
      this.startNoActivityTimeout(5000);
      this.addDocumentHandlers();
    },
    onActivityTimeoutDisabled() {
      this.visible = true;
      this.clearNoActivityTimeout();
      this.removeDocumentHandlers();
    },
    clearNoActivityTimeout() {
      if (this.noActivityTimeout) {
        clearTimeout(this.noActivityTimeout);
        this.noActivityTimeout = null;
      }
    },
    startNoActivityTimeout(timeout = 3000) {
      this.clearNoActivityTimeout();
      this.noActivityTimeout = setTimeout(() => {
        this.visible = false;
      }, timeout);
    },
  },
};
</script>

<style lang="stylus">
.navigation-arrow {
  width: max-content;

  &__button {
    border-radius: 50%;
    display: flex;
    height: 100px;
    transition: $nebula-transition-default;
    width: 50px;
    z-index: 211;

    &--next {
      align-items: center;
      border-radius: 110px 0 0 110px;
      opacity: 1;

      .navigation-arrow--hidden & {
        opacity: 0;
      }
    }

    &--prev {
      align-items: center;
      border-radius: 0 110px 110px 0;
      opacity: 1;

      .navigation-arrow--hidden & {
        opacity: 0;
      }
    }

    svg.nebula-button__icon {
      height: 30px;
      width: 30px;
    }
  }

  &__mystery-theme-button {
    $icon-color = #5876EF; // custom color
    background: none;
    border-radius: 50%;
    height: 170px;
    width: 170px

    svg.nebula-button__icon {
      fill: $icon-color;
      height: 170px;
      transition: transform .2s;
      width: 170px;
      &:hover {
        transform: scale(1.2);
      }
    }
    &:hover,
    &:focus {
      background: none;
      svg.nebula-button__icon {
        fill: $icon-color;
      }
    }
  }

  html[dir="rtl"] & {
    &__button {
      &--prev {
        border-radius: 110px 0 0 110px;
      }

      &--next {
        border-radius: 0 110px 110px 0;
      }
    }
  }
}
</style>
